import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { contactValidationSelectors, contactValidationOperations } from '@panel/core/src/state/ducks/contactValidation';
import Loader from '../../SimpleLoader';
import { getColor } from '../../../core/themev3';
import { sendCustomTrackingInfo } from '../../sendTrackingInfo';
import Checkbox from '../../Input/InputCheckbox';
import { getDevice, getPublisherTypeName } from '../../../helpers';
import { InputForm } from './InputForm';
import Alert from '../../Alert/Alert';
import { dataContactAdapter } from './dataContactAdapter';
import { sendProcessUserPhones, getValidBorder } from './helpers';
import { Container, CTA, TooltipText, LoaderWrapper } from './styles';

const ContactValidationModal = ({ onClose, setFinishedAction = () => {}, publisherTypeId = '2' }) => {
  const device = getDevice();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [contactData, setContactData] = useState({});
  const [showBranchPhone, setShowBranchPhone] = useState(false);
  const [checked, setChecked] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({ firstPhone: false, secondPhone: false, whatsappPhone: false });
  const [emptyInputError, setEmptyInputError] = useState(false);
  const [editedValue, setEditedValue] = useState({ firstPhone: false, secondPhone: false, whatsappPhone: false });
  const [hadPhoneType, setHadPhoneType] = useState(false);
  const [delayedLoading, setDelayedLoading] = useState(true);

  // Se consume data del servicio:
  const {
    getContactValidationData,
    updatedUserPhones,
    hasInvalidFormatPhone,
    isUpdatedUserPhones,
    isLoading,
    updateContactError
  } = useSelector(contactValidationSelectors.getContactValidationState);
  const { userPhones, branchPhone, isBranchPhone, email } = getContactValidationData;

  // Datos del Adapter:
  const phone1 = contactData?.firstPhone?.number;
  const phoneType1 = contactData?.firstPhone?.type;
  const phonePrefix1 = contactData?.firstPhone?.prefix;
  const phoneValid1 = contactData?.firstPhone?.isValid;

  const phone2 = contactData?.secondPhone?.number;
  const phoneType2 = contactData?.secondPhone?.type;
  const phonePrefix2 = contactData?.secondPhone?.prefix;
  const phoneValid2 = contactData?.secondPhone?.isValid;

  const phone3 = contactData?.whatsappPhone?.number;
  const phoneType3 = contactData?.whatsappPhone?.type;
  const phonePrefix3 = contactData?.whatsappPhone?.prefix;
  const phoneValid3 = contactData?.whatsappPhone?.isValid;

  // Se valida que exista branchPhone y que no tenga 'PHONE' ni 'CELL_PHONE':
  const hasOnlyContactBranch =
    branchPhone &&
    !userPhones.find(phone => phone.type === 'PHONE') &&
    !userPhones.find(phone => phone.type === 'CELL_PHONE');

  useEffect(() => {
    if (isLoading) {
      setDelayedLoading(true);
    } else {
      // Delay para evitar parpadeo en carga inicial
      setTimeout(() => setDelayedLoading(false), 200);
    }
  }, [isLoading]);

  useEffect(() => {
    // Primer FETCH para actualizar el estado con los datos del Get (proveniente de la base):
    setContactData(dataContactAdapter(userPhones));
    sendCustomTrackingInfo({
      event: 'impression_modal',
      content_group: 'Panel',
      modal_name: 'Verificación de Datos'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Segundo FETCH para el update de los datos:
    if (isUpdatedUserPhones && updatedUserPhones) {
      setContactData(dataContactAdapter(updatedUserPhones, hadPhoneType));
      setEditedValue({ firstPhone: false, secondPhone: false, whatsappPhone: false });
    }
    setChecked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatedUserPhones, updatedUserPhones, hadPhoneType]);

  useEffect(() => {
    setShowBranchPhone(hasOnlyContactBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOnlyContactBranch]);

  useEffect(() => {
    // Si el primer tipo de teléfono es un 'PHONE' se setea en true para que en el próximo update se cambie el tipo de ordenamiento en el adapter:
    setHadPhoneType(phoneType1 === 'PHONE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneType1]);

  useEffect(() => {
    // Si se realiza actualización o confirmación de los datos sin error de formato se dipara SNACKBAR de Éxito:
    if (isUpdatedUserPhones && !hasInvalidFormatPhone) {
      setFinishedAction({
        action: 'updateUserPhones',
        result: 'SUCCESS',
        component: t('panel.contactValidation.modal.snackbar.SUCCESS'),
        keepOpen: false
      });
      onClose();
    }
    // Si falla el servicio del update se dispara SNACKBAR de Error:
    if (updateContactError) {
      setFinishedAction({
        action: 'updateUserPhones',
        result: 'ERROR',
        component: t('panel.contactValidation.modal.snackbar.ERROR'),
        keepOpen: false
      });
      onClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatedUserPhones, updateContactError, hasInvalidFormatPhone]);

  const handleOnSubmit = () => {
    if (checked) {
      if (phone1 !== '' || showBranchPhone) {
        dispatch(contactValidationOperations.updateContactValidation(sendProcessUserPhones(contactData)));
        sendCustomTrackingInfo({
          event: 'click_modal',
          content_group: 'Panel',
          item_id: 'verificación-guardar'
        });
      } else {
        setEmptyInputError(true);
      }
    }
  };

  const handleOnChange = e => {
    const { value, id } = e.target;
    setContactData({
      ...contactData,
      [id]: { ...contactData[id], number: value.replace(/[^\d]/gi, '') }
    });
    setChecked(false);
    setEditedValue({ ...editedValue, [id]: true });
    // Se oculta el mensaje de Error (*Camplo Obligatorio) cuando el valor del primer input es !== '':
    if (id === 'firstPhone' && value !== '') setEmptyInputError(false);
  };

  const handleSetPhoneCode = (inputPhoneId, phoneCode) => {
    setContactData({
      ...contactData,
      [inputPhoneId]: { ...contactData[inputPhoneId], prefix: phoneCode }
    });
    setChecked(false);
  };

  const handleAddPhonesCTA = () => {
    setShowBranchPhone(false);
    setChecked(false);
    sendCustomTrackingInfo({
      event: 'click_modal',
      content_group: 'Panel',
      item_id: 'verificación-reemplazar'
    });
  };

  /**
   * La función evalúa el tipo de input para saber la traducción necesaria que debe devolver
   * @param {string} inputId
   * @returns devuelve un componente de styled components con la tradución necesaria
   */
  const getTooltipText = inputId => {
    if (inputId === 'whatsappPhone') {
      return <TooltipText>{t('panel.contactValidation.modal.form.whatsapp.tooltip')}</TooltipText>;
    }
    return (
      <TooltipText>
        {t(`panel.contactValidation.modal.form.${isBranchPhone ? 'branch' : 'publisher'}.tooltip`, {
          publisherTypeId: getPublisherTypeName[publisherTypeId]
        })}
      </TooltipText>
    );
  };

  return (
    <>
      {delayedLoading && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
      <Container device={device}>
        <div>
          <h3 data-panel='panel-contactValidation-title'>{t('panel.contactValidation.modal.title')}</h3>
          <p className='subtitle' data-panel='panel-contactValidation-subtitle'>
            {t('panel.contactValidation.modal.subtitle')}
          </p>
          <p className='subtitle-user' data-panel='panel-contactValidation-subtitle-user'>
            {t('panel.contactValidation.modal.subtitle.user', { userEmail: email })}
          </p>
        </div>
        <p className='form-title' data-panel='panel-contactValidation-textInfo-modal'>
          {t('panel.contactValidation.modal.form.title')}
        </p>

        <div className='form-container' data-panel='panel-contactValidation-form'>
          {showBranchPhone ? (
            <div className='inputBranch'>
              <InputForm
                device={device}
                type='text'
                inputPhoneId={isBranchPhone ? 'branch' : 'publisher'}
                number={branchPhone || ''}
                phoneType={isBranchPhone ? 'branch' : 'publisher'}
                publisherTypeId={getPublisherTypeName[publisherTypeId]}
                getTooltipText={getTooltipText('branch')}
                withLabelTooltip
                withoutPhoneCode
                disabled
              />
              <Alert
                dataPanel='panel-alert-phoneBranch-infoText'
                message={t(`panel.contactValidation.modal.info.${isBranchPhone ? 'branch' : 'publisher'}`, {
                  publisherTypeId: getPublisherTypeName[publisherTypeId]
                })}
                minHeight='45px'
                fontSize='14px'
                marginRightIcon='0px'
                alignContent='center'
                withoutBorder
                padding='9px 16px 9px 6px'
                margins='12px 0 0 0'
              >
                <div
                  data-panel='panel-alert-phoneBranch-CTA'
                  aria-hidden='true'
                  onClick={handleAddPhonesCTA}
                  className='ctaSetInput'
                >
                  {t('panel.contactValidation.modal.info.cta')}
                </div>
              </Alert>
            </div>
          ) : (
            <>
              <InputForm
                device={device}
                inputPhoneId='firstPhone'
                number={phone1 || ''}
                phonePrefix={phonePrefix1}
                phoneType={phoneType1}
                isPhoneValid={phoneValid1}
                handleOnChange={handleOnChange}
                handleSetPhoneCode={handleSetPhoneCode}
                getValidBorder={getValidBorder(phoneValid1, editedValue.firstPhone, emptyInputError)}
                editedValue={editedValue.firstPhone}
                dropdownOpen={dropdownOpen.firstPhone}
                setDropdownOpen={() =>
                  setDropdownOpen({ whatsappPhone: false, secondPhone: false, firstPhone: !dropdownOpen.firstPhone })
                }
                emptyInputError={emptyInputError}
                isLoading={delayedLoading}
              />
              <InputForm
                device={device}
                inputPhoneId='secondPhone'
                number={phone2 || ''}
                phonePrefix={phonePrefix2}
                phoneType={phoneType2}
                isPhoneValid={phoneValid2}
                handleOnChange={handleOnChange}
                handleSetPhoneCode={handleSetPhoneCode}
                getValidBorder={getValidBorder(phoneValid2, editedValue.secondPhone)}
                editedValue={editedValue.secondPhone}
                dropdownOpen={dropdownOpen.secondPhone}
                setDropdownOpen={() =>
                  setDropdownOpen({ whatsappPhone: false, firstPhone: false, secondPhone: !dropdownOpen.secondPhone })
                }
                isLoading={delayedLoading}
              />
            </>
          )}
          <InputForm
            device={device}
            inputPhoneId='whatsappPhone'
            number={phone3 || ''}
            phonePrefix={phonePrefix3}
            phoneType={phoneType3}
            isPhoneValid={phoneValid3}
            handleOnChange={handleOnChange}
            handleSetPhoneCode={handleSetPhoneCode}
            getValidBorder={getValidBorder(phoneValid3, editedValue.whatsappPhone)}
            editedValue={editedValue.whatsappPhone}
            dropdownOpen={dropdownOpen.whatsappPhone}
            setDropdownOpen={() =>
              setDropdownOpen({ firstPhone: false, secondPhone: false, whatsappPhone: !dropdownOpen.whatsappPhone })
            }
            getTooltipText={getTooltipText('whatsappPhone')}
            withLabelTooltip
            isLoading={delayedLoading}
          />

          <div className='confirmData' aria-hidden='true'>
            <Checkbox
              name=''
              checked={checked}
              fillColor={getColor('primary')}
              readOnly
              cursorType='pointer'
              margin='2px 5px 4px 0px'
              onClick={() => setChecked(!checked)}
            />
            <p data-panel='panel-contactValidation-confirmData'>{t('panel.contactValidation.modal.checkbox')}</p>
          </div>

          <CTA
            data-panel='panel-contactValidation-buttonCTA'
            enabled={checked}
            marginTop='8px'
            onClick={handleOnSubmit}
          >
            {t('panel.contactValidation.modal.cta.confirm')}
          </CTA>

          <CTA
            data-panel='panel-contactValidation-buttonCTA'
            className='noBorderButton'
            onClick={() => {
              onClose();
              sendCustomTrackingInfo({
                event: 'click_modal',
                content_group: 'Panel',
                item_id: 'verificación-omitir'
              });
            }}
            enabled
          >
            {t('panel.contactValidation.modal.cta.skip')}
          </CTA>
        </div>
      </Container>
    </>
  );
};

ContactValidationModal.propTypes = {
  onClose: PropTypes.func.isRequired
};

export { ContactValidationModal };
